import { forwardRef } from 'react';
import type { ReactNode, ElementType } from 'react';
import { cn } from '@/helpers/shadcn';

// Base classes shared across typography variants
const baseClasses = 'scroll-m-20';
const headingBaseClasses = 'font-semibold py-2';
const bodyBaseClasses = 'leading-relaxed';

// Typography variant definitions inspired by Lovevery's clean, modern design
const typographyVariants = {
  // Headings - Clean, modern hierarchy with excellent readability
  h1: cn(
    baseClasses,
    headingBaseClasses,
    'text-3xl font-bold text-stone-900 lg:text-4xl xl:text-5xl'
  ),
  // Extra-large display heading for hero/title use
  display: cn(
    baseClasses,
    'py-2 font-extrabold text-stone-900 text-5xl lg:text-6xl xl:text-[72px]'
  ),
  h2: cn(
    baseClasses,
    headingBaseClasses,
    'text-xl font-bold text-stone-900 first:mt-0 lg:text-2xl xl:text-3xl'
  ),
  h3: cn(
    baseClasses,
    headingBaseClasses,
    'text-lg font-bold text-stone-800 lg:text-xl'
  ),
  h4: cn(
    baseClasses,
    headingBaseClasses,
    'text-base text-stone-800 lg:text-lg'
  ),
  h5: cn(
    baseClasses,
    headingBaseClasses,
    'text-sm text-stone-700 lg:text-base'
  ),
  h6: cn(baseClasses, headingBaseClasses, 'text-xs text-stone-700 lg:text-sm'),

  // Body text - Optimized for readability and warmth
  p: cn(bodyBaseClasses, 'text-base text-stone-700 [&:not(:first-child)]:mt-4'),
  lead: cn(bodyBaseClasses, 'text-lg text-stone-600 lg:text-xl'),
  large: cn(bodyBaseClasses, 'text-lg font-medium text-stone-700'),
  small: cn(bodyBaseClasses, 'text-sm text-stone-600'),
  muted: cn(bodyBaseClasses, 'text-sm text-stone-500'),

  // Special variants
  blockquote: cn(
    bodyBaseClasses,
    'mt-6 border-l-4 border-stone-300 pl-6 italic text-stone-600'
  ),
  code: 'relative rounded-md bg-stone-100 px-2 py-1 font-mono text-sm font-medium text-stone-800',
  list: cn(
    bodyBaseClasses,
    'my-4 ml-6 list-disc space-y-2 text-stone-700 [&>li]:leading-relaxed'
  ),
} as const;

// Default HTML elements for each variant
const defaultElements = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  p: 'p',
  lead: 'p',
  large: 'div',
  small: 'small',
  muted: 'p',
  blockquote: 'blockquote',
  code: 'code',
  list: 'ul',
  display: 'h1',
} as const;

export type TypographyVariant = keyof typeof typographyVariants;

export interface TypographyProps {
  variant?: TypographyVariant;
  as?: ElementType;
  className?: string;
  children?: ReactNode;
}

export const Typography = forwardRef<HTMLElement, TypographyProps>(
  ({ variant = 'p', as, className, children, ...props }, ref) => {
    const Component = as || defaultElements[variant];

    return (
      <Component
        ref={ref}
        className={cn(typographyVariants[variant], className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Typography.displayName = 'Typography';

// Export individual components for convenience
export const Heading1 = forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="h1" ref={ref} {...props} />);

export const Heading2 = forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="h2" ref={ref} {...props} />);

export const Heading3 = forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="h3" ref={ref} {...props} />);

export const Heading4 = forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="h4" ref={ref} {...props} />);

export const Heading5 = forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="h5" ref={ref} {...props} />);

export const Heading6 = forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="h6" ref={ref} {...props} />);

export const Paragraph = forwardRef<
  HTMLParagraphElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="p" ref={ref} {...props} />);

export const Lead = forwardRef<
  HTMLParagraphElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="lead" ref={ref} {...props} />);

export const Large = forwardRef<
  HTMLDivElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="large" ref={ref} {...props} />);

export const Small = forwardRef<HTMLElement, Omit<TypographyProps, 'variant'>>(
  (props, ref) => <Typography variant="small" ref={ref} {...props} />
);

export const Muted = forwardRef<
  HTMLParagraphElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="muted" ref={ref} {...props} />);

export const Blockquote = forwardRef<
  HTMLQuoteElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="blockquote" ref={ref} {...props} />);

export const InlineCode = forwardRef<
  HTMLElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="code" ref={ref} {...props} />);

export const List = forwardRef<
  HTMLUListElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => <Typography variant="list" ref={ref} {...props} />);

export const Display = forwardRef<
  HTMLHeadingElement,
  Omit<TypographyProps, 'variant'>
>((props, ref) => (
  <Typography variant="display" as="h1" ref={ref} {...props} />
));

Heading1.displayName = 'Heading1';
Heading2.displayName = 'Heading2';
Heading3.displayName = 'Heading3';
Heading4.displayName = 'Heading4';
Heading5.displayName = 'Heading5';
Heading6.displayName = 'Heading6';
Paragraph.displayName = 'Paragraph';
Lead.displayName = 'Lead';
Large.displayName = 'Large';
Small.displayName = 'Small';
Muted.displayName = 'Muted';
Blockquote.displayName = 'Blockquote';
InlineCode.displayName = 'InlineCode';
List.displayName = 'List';
Display.displayName = 'Display';
